import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { REHYPE_PLUGINS } from '@/components/ui/smoothdown/plugins';
import {
  type EchoNotesSection,
  EchoNotesSections,
  type EchoNotesTimestamps,
} from '@/features/echonotes/echonotes-query';

import { EchoNotesSectionSelector } from './EchoNotesSectionSelector';
import { EchoNotesTimestampSelector } from './EchoNotesTimestampSelector';
import { useEchoNotes } from './use-echonotes';

interface EchoNotesViewerProps {
  section?: EchoNotesSection;
  maxLength?: number;
}

export function EchoNotesViewer({
  section: fixedSection,
  maxLength,
}: EchoNotesViewerProps = {}) {
  const [selectedSection, setSelectedSection] = useState<EchoNotesSection>(
    fixedSection ?? EchoNotesSections.TOPIC_HIGHLIGHTS
  );
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const activeSection = fixedSection ?? selectedSection;

  const { echonotes, timestamps, isLoading, error } = useEchoNotes(
    selectedId,
    activeSection
  );

  useEffect(() => {
    setSelectedId(undefined);
  }, [activeSection]);

  const handleSectionChange = (section: EchoNotesSection) => {
    setSelectedSection(section);
  };

  const handleTimestampSelect = (timestamp: EchoNotesTimestamps) => {
    setSelectedId(timestamp.id);
  };

  const currentTimestampLabel = echonotes
    ? new Date(echonotes.latest_included_message_timestamp).toLocaleString()
    : 'Select version';

  const messageStyle = 'p-4 text-foreground-muted text-sm select-text';
  const textLength = echonotes?.full_text?.length ?? 0;

  return (
    <YStack className="min-w-0 gap-3 w-full">
      <XStack className="justify-between items-center gap-2 flex-wrap">
        {!fixedSection && (
          <EchoNotesSectionSelector
            selectedSection={activeSection}
            onSectionChange={handleSectionChange}
          />
        )}

        <EchoNotesTimestampSelector
          timestamps={timestamps}
          currentTimestampLabel={currentTimestampLabel}
          onTimestampSelect={handleTimestampSelect}
        />
      </XStack>

      <div className="min-w-0 border border-border bg-surface">
        {isLoading ? (
          <div className={messageStyle}>Loading...</div>
        ) : error ? (
          <div className={messageStyle}>
            Document could not be loaded:{' '}
            {error instanceof Error ? error.message : String(error)}
          </div>
        ) : !echonotes ? (
          <div className={messageStyle}>Document does not exist.</div>
        ) : !echonotes.full_text ? (
          <div className={messageStyle}>Document is empty.</div>
        ) : (
          <ScrollArea className={fixedSection ? 'h-full' : 'h-[16rem]'}>
            <div className="p-4 prose prose-bird prose-sm max-w-none select-text">
              <ReactMarkdown
                rehypePlugins={REHYPE_PLUGINS}
                remarkPlugins={[remarkGfm]}
              >
                {echonotes.full_text}
              </ReactMarkdown>
            </div>
          </ScrollArea>
        )}
      </div>

      {maxLength !== undefined && echonotes?.full_text && (
        <span className="text-xs text-foreground-muted">
          {textLength}/{maxLength} characters
        </span>
      )}
    </YStack>
  );
}
